/**
 * Client-side SEO meta manager for SPA navigation.
 */
export function setMetaTag(name, content, attr = 'name') {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function setLinkTag(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function removeMetaProperty(property) {
  document.querySelectorAll(`meta[property="${property}"]`).forEach((el) => el.remove());
}

export function applySeo(seo = {}) {
  const title = seo.seoTitle || seo.metaTitle || seo.title || 'Smart Telecom Store';
  const description = seo.metaDescription || seo.description || '';
  const robotsIndex = seo.robotsIndex !== false;
  const robotsFollow = seo.robotsFollow !== false;

  document.title = title;
  setMetaTag('description', description);

  setMetaTag('robots', `${robotsIndex ? 'index' : 'noindex'}, ${robotsFollow ? 'nofollow' : 'follow'}`);

  if (seo.canonicalUrl) setLinkTag('canonical', seo.canonicalUrl);

  const ogFields = [
    ['og:title', seo.ogTitle || title, 'property'],
    ['og:description', seo.ogDescription || description, 'property'],
    ['og:image', seo.ogImage, 'property'],
    ['og:type', seo.ogType || 'website', 'property'],
    ['og:url', seo.canonicalUrl, 'property'],
  ];
  ogFields.forEach(([name, content, attr]) => setMetaTag(name, content, attr));

  const twitterFields = [
    ['twitter:card', 'summary_large_image'],
    ['twitter:title', seo.twitterTitle || seo.ogTitle || title],
    ['twitter:description', seo.twitterDescription || seo.ogDescription || description],
    ['twitter:image', seo.twitterImage || seo.ogImage],
  ];
  twitterFields.forEach(([name, content]) => setMetaTag(name, content));

  if (seo.keywords) {
    const kw = Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords;
    setMetaTag('keywords', kw);
  }
}

export function productToSeo(product) {
  if (!product) return {};
  return {
    seoTitle: product.seoTitle || product.metaTitle || `${product.name} | Smart Telecom Store`,
    metaDescription: product.metaDescription || product.description,
    canonicalUrl: product.canonicalUrl,
    robotsIndex: product.robotsIndex,
    robotsFollow: product.robotsFollow,
    ogTitle: product.ogTitle,
    ogDescription: product.ogDescription,
    ogImage: product.ogImage || product.image,
    ogType: 'product',
    twitterTitle: product.twitterTitle,
    twitterDescription: product.twitterDescription,
    twitterImage: product.twitterImage,
    keywords: product.keywords,
  };
}

export async function fetchSiteSettings() {
  try {
    const res = await fetch('/api/site-settings');
    if (res.ok) return res.json();
  } catch (e) {
    console.warn('Could not fetch site settings', e);
  }
  return { useSlugUrls: false, useCmsPages: false };
}

export function getProductUrl(product, settings = {}) {
  if (!product) return '/';
  const id = product._id || product.id;
  if (settings.useSlugUrls && product.slug) {
    return `/product/${product.slug}`;
  }
  return `/product/${id}`;
}

export async function fetchCmsPage(pageKey) {
  try {
    const res = await fetch(`/api/pages/key/${pageKey}`);
    if (res.ok) return res.json();
  } catch (e) {
    console.warn(`CMS page ${pageKey} not available`, e);
  }
  return null;
}
